import { makeEventNamespace } from '@os/dom/events'
import { fetchProductResult, productCardHtml } from '@os/product-card-html'
import { findElements, findOneElement } from '@os/dom/traversal'

/**
 * `<product-upsell>` — the product page's "Pairs well with" / "Save with sets"
 * tabs and the optional Rebuy recommendations.
 *
 * Hand-authored custom element module, included next to its markup with
 * `<script src="{{ 'product-upsell.js' | asset_url }}" type="module">`. Shared
 * behavior comes from the `@os/*` modules resolved by the theme import map. See
 * `docs/architecture.md`.
 *
 * The tab panels and their product cards are server-rendered; this element
 * wires the tab switching and, when Rebuy is enabled, fetches the
 * recommendations and renders the cards at runtime.
 *
 * Markup contract:
 * - `[data-product-upsell-tab]` / `[data-product-upsell-panel]` pairs keyed by
 *   the same value; tabs are roving (Arrow/Home/End).
 * - `[data-carousel-track]` receives the rendered Rebuy `<core-product-widget>`
 *   cards; a `<scroll-carousel>` wraps it (initially `hidden`, re-initialised
 *   once the slides are rendered).
 * - `[data-product-upsell-loading]` is the Rebuy busy indicator.
 *
 * The former zod `RebuyResponseSchema` validation is replaced by the native
 * `parseRebuyResponse` validator below. Cards are rendered through the shared
 * `@os/product-card-html` renderer.
 *
 * Replaces the React `react-product-upsell` snippet (metafield tabs + Rebuy).
 */

const REBUY_LIMIT = 3

const isObject = (value) =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/**
 * Native replacement for the former zod `RebuyResponseSchema.safeParse`.
 * `data` is optional (defaults to `[]`); each item needs a numeric `id` and a
 * string `handle`, with an optional string `tags`.
 *
 * @param {unknown} raw
 * @returns {{ data: object[] } | null}
 */
const parseRebuyResponse = (raw) => {
  if (!isObject(raw)) {
    return null
  }

  const data = raw.data ?? []

  if (!Array.isArray(data)) {
    return null
  }

  const items = data.map((item) => {
    if (!isObject(item)) {
      return null
    }

    if (typeof item.id !== 'number' || typeof item.handle !== 'string') {
      return null
    }

    if (item.tags !== undefined && typeof item.tags !== 'string') {
      return null
    }

    return { id: item.id, handle: item.handle, tags: item.tags ?? '' }
  })

  if (items.includes(null)) {
    return null
  }

  return { data: items }
}

const parseTags = (raw) =>
  raw
    .split(',')
    .map((tag) => tag.trim().toLowerCase())
    .filter(Boolean)

const hasIgnoredTag = (tags, ignored) => {
  if (ignored.length === 0) {
    return false
  }

  const productTags = tags.split(',').map((tag) => tag.trim().toLowerCase())

  return ignored.some((tag) => productTags.includes(tag))
}

class ProductUpsell extends HTMLElement {
  #cleanup = null

  connectedCallback() {
    if (this.#cleanup != null) {
      return
    }

    const namespace = makeEventNamespace()
    const controller = new AbortController()

    const tabs = findElements(this, '[data-product-upsell-tab]')
    const panels = findElements(this, '[data-product-upsell-panel]')

    if (tabs.length > 0 && panels.length > 0) {
      const setActive = (key) => {
        tabs.forEach((tab) => {
          const isActive = tab.getAttribute('data-product-upsell-tab') === key

          tab.setAttribute('aria-selected', isActive ? 'true' : 'false')
          tab.setAttribute('tabindex', isActive ? '0' : '-1')
        })

        panels.forEach((panel) => {
          const isActive =
            panel.getAttribute('data-product-upsell-panel') === key

          panel.setAttribute('aria-hidden', isActive ? 'false' : 'true')
        })
      }

      tabs.forEach((tab) => {
        namespace.addDirectEventListener(tab, 'click', () => {
          const key = tab.getAttribute('data-product-upsell-tab')

          if (key) {
            setActive(key)
          }
        })

        namespace.addDirectEventListener(tab, 'keydown', (_, evt) => {
          if (!(evt instanceof KeyboardEvent)) {
            return
          }

          const currentIndex = tabs.indexOf(tab)
          let targetIndex = -1

          if (evt.key === 'ArrowRight') {
            targetIndex = (currentIndex + 1) % tabs.length
          } else if (evt.key === 'ArrowLeft') {
            targetIndex = (currentIndex - 1 + tabs.length) % tabs.length
          } else if (evt.key === 'Home') {
            targetIndex = 0
          } else if (evt.key === 'End') {
            targetIndex = tabs.length - 1
          }

          if (targetIndex < 0) {
            return
          }

          evt.preventDefault()
          const targetTab = tabs[targetIndex]
          const targetKey = targetTab?.getAttribute('data-product-upsell-tab')

          if (targetTab && targetKey) {
            setActive(targetKey)
            targetTab.focus()
          }
        })
      })

      const initialTab = tabs.find(
        (tab) => tab.getAttribute('aria-selected') === 'true'
      )
      const initialKey = initialTab?.getAttribute('data-product-upsell-tab')

      if (initialKey) {
        setActive(initialKey)
      }
    }

    // The Rebuy path is scoped to its own container so it never touches the
    // server-rendered metafield tab carousels.
    const rebuy = findOneElement(this, '[data-product-upsell-rebuy]')
    const track =
      rebuy === null ? null : findOneElement(rebuy, '[data-carousel-track]')
    const carousel =
      rebuy === null ? null : findOneElement(rebuy, 'scroll-carousel')
    const loading =
      rebuy === null
        ? null
        : findOneElement(rebuy, '[data-product-upsell-loading]')

    const apiKey = this.getAttribute('data-rebuy-api-key') ?? ''
    const productId = Number(this.getAttribute('data-product-id') ?? '')
    const tagsToIgnore = parseTags(
      this.getAttribute('data-tags-to-ignore') ?? ''
    )
    const shopLabel = this.getAttribute('data-label-shop') ?? ''
    const reviewsLabel = this.getAttribute('data-label-reviews') ?? ''
    const linkLabel = this.getAttribute('data-label-link') ?? ''

    const finish = () => {
      if (loading !== null) {
        loading.hidden = true
      }
    }

    const runRebuy = async () => {
      if (
        rebuy === null ||
        track === null ||
        carousel === null ||
        apiKey === '' ||
        !Number.isFinite(productId)
      ) {
        return
      }

      try {
        const res = await fetch(
          `https://rebuyengine.com/api/v1/products/recommended?key=${encodeURIComponent(
            apiKey
          )}&shopify_product_ids=${productId}&limit=${REBUY_LIMIT}`,
          { signal: controller.signal }
        )

        if (!res.ok) {
          finish()
          return
        }

        const parsed = parseRebuyResponse(await res.json())

        if (parsed === null) {
          finish()
          return
        }

        const handles = parsed.data
          .filter((item) => !hasIgnoredTag(item.tags, tagsToIgnore))
          .slice(0, REBUY_LIMIT)
          .map((item) => item.handle)

        const results = (
          await Promise.all(
            handles.map((handle) =>
              fetchProductResult(`/products/${handle}`, controller.signal)
            )
          )
        ).filter((result) => result !== null)

        if (controller.signal.aborted) {
          return
        }

        if (results.length === 0) {
          finish()
          return
        }

        track.innerHTML = results
          .map(
            (result) =>
              `<div class="product-upsell__card" data-carousel-slide>${productCardHtml(
                result,
                {
                  add: shopLabel,
                  reviews: reviewsLabel,
                  linkLabel
                }
              )}</div>`
          )
          .join('')

        carousel.hidden = false
        finish()

        // The carousel connected with an empty track, so re-initialise it now
        // that the slides exist (and it is visible, so measurements are valid).
        const parent = carousel.parentElement

        if (parent !== null) {
          carousel.remove()
          parent.appendChild(carousel)
        }
      } catch {
        if (!controller.signal.aborted) {
          finish()
        }
      }
    }

    void runRebuy()

    this.#cleanup = () => {
      controller.abort()
      namespace.destroy()
    }
  }

  disconnectedCallback() {
    this.#cleanup?.()
    this.#cleanup = null
  }
}

// Sections can be re-rendered by the theme editor; defining twice throws.
if (!customElements.get('product-upsell')) {
  customElements.define('product-upsell', ProductUpsell)
}
